import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AdminPanelPage } from '../AdminPanelPage'
import { BreadcrumbProps, Button, Icon, InlineEditableText, Modal, Tooltip, useToast, DashboardGrid, DashboardGridItem, DashboardGridLayout } from '@aidkitorg/component-library'
import { DashboardBuilderPanel } from './components/DashboardBuilderPanel'
import { useLocalizedStrings } from '../../Localization'
import { useDashboards } from '../../Contexts/DashboardsContext'
import { DashboardComponentCard } from './components/DashboardComponents/DashboardComponentCard'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useAsyncEffect } from '../../Hooks/AsyncEffect'

export const DashboardBuilderPage = () => {
  const L = useLocalizedStrings();
  const { dashboardId } = useParams<{ dashboardId?: string }>()
  const location = useLocation()

  const newDashboard = location.pathname === '/admin-panel/dashboards/new'
  const isEditMode = Boolean(dashboardId)

  const { 
    dashboardsById,
    stagedComponents, 
    setStagedComponents, 
    createDashboard, 
    updateDashboard,
    fetchDashboardById 
  } = useDashboards()
  const { toast } = useToast()
  const history = useHistory()

  const [originalTitle, setOriginalTitle] = useState<string>('My Dashboard')
  const [title, setTitle] = useState<string>('My Dashboard')
  const [description, setDescription] = useState<string>('')
  const [dataSourceModalOpen, setDataSourceModalOpen] = useState(isEditMode ? false : true)
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false)

  const GRID_ROW_HEIGHT = 160
  const GRID_COLS = 6
  const GRID_WIDTH = 1200

  const sizeByKind: Record<string, { w: number, h: number, minW: number, minH: number }> = {
    'Hero Number': { w: 1, h: 1, minW: 1, minH: 1 },
    'Bar Chart':   { w: 4, h: 3, minW: 2, minH: 3 },
    'Pie Chart':   { w: 4, h: 3, minW: 2, minH: 3 },
    'Map':         { w: 5, h: 4, minW: 2, minH: 3 },
  };

  const getSizeForKind = (kind: string) => sizeByKind[kind] || { w: 2, h: 2 };

  // Transform staged components into DashboardGridItem format
  const gridItems: Array<DashboardGridItem<any>> = stagedComponents.map((component) => ({
    id: component.id,
    data: component,
    layout: ("style" in component && component.style) ? component.style : undefined
  }));

  const [currentLayout, setCurrentLayout] = useState<DashboardGridLayout[]>([]);

  const getDefaultSize = (item: DashboardGridItem<any>) => {
    return getSizeForKind(item.data.config.kind);
  };

  const renderGridItem = (item: DashboardGridItem<any>, dimensions: { width: number; height: number }) => (
    <DashboardComponentCard 
      id={item.data.id}
      isEditMode={isEditMode || newDashboard}
      component={item.data.config}
      width={dimensions.width}
      height={dimensions.height}
    />
  );
  
  useAsyncEffect(async () => {
    if (isEditMode && dashboardId) {
      const loadExistingDashboard = async () => {
        setIsLoadingDashboard(true)
        try {
          await fetchDashboardById(dashboardId)
        } catch (e) {
          // TODO: notify user of error while loading dashboard
          history.push('/admin-panel/dashboards')
          setIsLoadingDashboard(false)
        }
      }
      await loadExistingDashboard()
    }
  }, [isEditMode, dashboardId])

  useEffect(() => {
    if (isEditMode && dashboardId && dashboardsById[dashboardId] && isLoadingDashboard) {
      const dashboard = dashboardsById[dashboardId]
      setTitle(dashboard.title)
      setOriginalTitle(dashboard.title)
      setDescription(dashboard.description || description)
      setStagedComponents(dashboard.components)
      setIsLoadingDashboard(false)
    }  
  }, [isEditMode, dashboardsById, isLoadingDashboard])

  useEffect(() => {
    return () => {
      setStagedComponents([]); // Clear components when unmounting
    };
  }, []);

  const createDashboardHandler = useCallback(async () => {
    const createdDashboard = await createDashboard(title, description, stagedComponents, currentLayout)
    if (createdDashboard) {
      setStagedComponents([])
      toast({
        title: L.admin_panel.dashboards.success,
        description: L.admin_panel.dashboards.dashboard_created,
        variant: 'success'
      })
        
      history.push("/admin-panel/dashboards/"+ createdDashboard)
    } else {
      toast({
        title: L.admin_panel.dashboards.error,
        description: L.admin_panel.dashboards.error_creating,
        variant: 'error'
      })
    }
  }, [title, description, stagedComponents, currentLayout, createDashboard])

  const createDashboardButton = useMemo(() => (
    <Button variant="primary" onClick={createDashboardHandler}>
      {L.admin_panel.dashboards.create_dashboard}
    </Button>
  ), [title, description, stagedComponents, currentLayout])


  const saveDashboardButton = useMemo(() => (
    <Button variant="primary" onClick={async () => {
      if (dashboardId) {
        const updatedDashboard = await updateDashboard(dashboardId, title, description, stagedComponents, currentLayout)
        if (updatedDashboard) {
          setStagedComponents([])
          toast({
            title: L.admin_panel.dashboards.success,
            description: L.admin_panel.dashboards.dashboard_saved,
            variant: 'success'
          })
        
          history.push("/admin-panel/dashboards/"+ updatedDashboard)
        } else {
          toast({
            title: L.admin_panel.dashboards.error,
            description: L.admin_panel.dashboards.error_saving,
            variant: 'error'
          })
        }
      }
    }}>
      {L.admin_panel.dashboards.save_dashboard}
    </Button>
  ), [dashboardId, title, description, stagedComponents, currentLayout])

  const pageActionButtons = [
    <Tooltip text={L.admin_panel.dashboards.select_data_source} asChild>
      <Button
        icon={<Icon role="decorative" type="solid" name="CircleStackIcon" />}
        variant="secondary"
        size="sm"
        aria-label={L.admin_panel.dashboards.select_data_source}
        onClick={() => { setDataSourceModalOpen(true) }}
      />
    </Tooltip>,    
    isEditMode ? saveDashboardButton : createDashboardButton
  ]
  
  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.dashboards,
      href: '/admin-panel/dashboards'
    },
    {
      label: isEditMode ? originalTitle : L.admin_panel.dashboards.dashboard_builder,
    }
  ]

  const DataSourceModal = () => {
    return (
      <Modal size='lg' title={L.admin_panel.dashboards.select_report} open={dataSourceModalOpen} onClose={() => setDataSourceModalOpen(false)}>
        {/* TODO: add in reports, for now just have applications */}
        <div className="text-sm text-gray-700 -mt-2 mb-4">{L.admin_panel.dashboards.choose_report_msg}</div>
        <div className="flex flex-col gap-y-4">
          <div className="uppercase text-sm text-gray-600 font-medium tracking-wider">{L.admin_panel.dashboards.applications}</div>
          <Button
            variant="secondary"
            className='!w-full !justify-start'
            // TODO: update this so that it actually changes the data source
            onClick={() => setDataSourceModalOpen(false)}
          >
            <div className="flex items-center gap-x-4 py-2">
              <div>
                <Icon strokeWidth={2} className="text-blue-700" name="DocumentTextIcon" role="decorative"/>
              </div>
              <div className="text-left flex flex-col gap-y-1">
                <div>{L.admin_panel.dashboards.application_statistics}</div>
                <div className="text-gray-700 font-normal">
                  {L.admin_panel.dashboards.applications_desc}
                </div>
              </div>
            </div>
          </Button>
        </div>
      </Modal>
    )
  }
  // TODO: add modal and report selection, for now, just build from an empty page
  
  const titleEditor = (<InlineEditableText
    as="h1"
    required
    label={L.admin_panel.dashboards.title}
    value={title}
    onSave={(value: string | number) => setTitle(String(value))}
    multiline
  />)
   
  const descriptionEditor = (
    <InlineEditableText 
      label={L.admin_panel.dashboards.dashboard_description}
      value={description}
      placeholder={L.admin_panel.dashboards.dashboard_desc_placeholder}
      onSave={(value: string | number) => setDescription(String(value))}
      multiline
    />
  )

  const gridEmptyState = (        
    <div className="flex-grow flex items-center justify-center h-64 text-gray-500">
      <div className="text-center">
        <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
        </svg>
        <div className="text-lg font-medium mb-2">{L.admin_panel.dashboards.build_your_dashboard}</div>
        <div>{L.admin_panel.dashboards.build_your_dashboard_msg}</div>
      </div>
    </div>
  )

  const updateComponentStyles = (layout: Array<DashboardGridLayout>) => {
    const updatedComponents = stagedComponents.map(component => {
      const matchingLayout = layout.find(item => item.i === component.id);

      if (matchingLayout) {
        return {
          ...component,
          style: matchingLayout,
        };
      }

      return component;
    });

    setStagedComponents(updatedComponents);
  };

  const GridArea = () => {
    return (
      <div className="flex-grow">
        <DashboardGrid
          items={gridItems}
          renderItem={renderGridItem}
          rowHeight={GRID_ROW_HEIGHT}
          isDraggable={true}
          isResizable={true}
          draggableHandle=".drag-handle"
          onLayoutChange={(layout: Array<DashboardGridLayout>) => {
            updateComponentStyles(layout)
            setCurrentLayout(layout)
          }}
          getDefaultSize={getDefaultSize}
          defaultGridCols={GRID_COLS}
          defaultGridWidth={GRID_WIDTH}
          emptyState={gridEmptyState}
        />
      </div>
    );
  }

  return (
    <AdminPanelPage
      title={titleEditor}
      description={descriptionEditor}
      pageActionButtons={pageActionButtons}
      breadcrumbs={breadcrumbs}
      keyboardShortcuts={[
        { key: 'B', description: L.admin_panel.reports.toggle_sidebar, modifier: 'meta' },
        { key: 'K', description: L.admin_panel.command_palette.keyboard_shortcut, modifier: 'meta' },
        { key: 'S', description: L.admin_panel.dashboards.save_dashboard, modifier: 'meta', callback: () => { createDashboardHandler() } },
      ]}
    >
      <div className="flex justify-between gap-4 w-full">
        { GridArea() }
        <div className="flex-shrink-0 w-80 h-full border rounded-md bg-white">
          <DashboardBuilderPanel/>
        </div>
        <DataSourceModal/>
      </div>
    </AdminPanelPage>
  )
}